import { observer } from 'mobx-react';
import { ParamParseKey, useNavigate, useParams } from 'react-router-dom';

import { useInvariableEffect } from '@servicetitan/react-hooks';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Button, Flex, Page } from '@servicetitan/anvil2';
import { CenteredSpinner } from '@servicetitan/marketing-ui';

import { withRoot } from '../../utils/with-root';
import { AccountManagementRoutes } from '../../utils/routes';
import { LoadStatus } from '../../../common/enums/load-status';
import { PartnerConfiguration } from './partner-configuration';
import { ManagePartnerStore } from '../../stores/manage-partner.store';
import { CenteredError } from '../../../common/components/centered-error/centered-error';

import * as Styles from './manage-partner.module.less';

export const ManagePartner = provide({ singletons: [ManagePartnerStore] })(
    observer(() => {
        const { externalPartnerId } =
            useParams<ParamParseKey<typeof AccountManagementRoutes.managePartner>>();

        const [
            {
                pageLoadStatus,
                init,
                saveConfigs,
                saveLoadStatus,
                formState,
                isFormChanged,
                resetConfiguration,
            },
        ] = useDependencies(ManagePartnerStore);

        useInvariableEffect(() => {
            init(externalPartnerId!);
        });

        const navigate = useNavigate();

        return (
            <Flex direction="column" grow={1}>
                <Page className={Styles.page}>
                    <Page.Header
                        title=""
                        breadcrumbs={[
                            {
                                children: 'Accounts Management',
                                onClick: () => navigate(withRoot(AccountManagementRoutes.partners)),
                            },
                            { children: 'Manage Partner' },
                        ]}
                    />
                    <Page.Content className={Styles.pageContent}>
                        <div className={Styles.contentWrapper}>
                            {pageLoadStatus === LoadStatus.Loading ? (
                                <CenteredSpinner />
                            ) : pageLoadStatus === LoadStatus.Error ? (
                                <CenteredError />
                            ) : (
                                <PartnerConfiguration />
                            )}
                        </div>
                    </Page.Content>
                    <Page.Footer justifyContent="flex-end">
                        <Flex gap={4}>
                            <Button onClick={resetConfiguration} disabled={!isFormChanged}>
                                Cancel
                            </Button>
                            <Button
                                appearance="primary"
                                onClick={saveConfigs}
                                loading={saveLoadStatus === LoadStatus.Loading}
                                disabled={
                                    formState.hasError ||
                                    saveLoadStatus === LoadStatus.Loading ||
                                    !isFormChanged
                                }
                            >
                                Save
                            </Button>
                        </Flex>
                    </Page.Footer>
                </Page>
            </Flex>
        );
    }),
);
